import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const StaffManagementList = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterText, setFilterText] = useState('');
  const [deletingStaffId, setDeletingStaffId] = useState(null);

  const fetchMyStaffData = useCallback(async () => {
    try {
      setLoading(true);
      // Use single API endpoint for both admin and regular users
      const response = await api.get('/api/staff');
      setStaffList(response.data);
    } catch (error) {
      console.error('Error fetching staff list:', error);
      setError('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    fetchMyStaffData();
  }, [isAuthenticated, user, navigate, fetchMyStaffData]);

  // Filter staff based on search input
  useEffect(() => {
    if (filterText.trim() && staffList.length > 0) {
      const filtered = staffList.filter(staff => 
        staff.staff_id?.toLowerCase().includes(filterText.toLowerCase()) ||
        staff.id?.toString().toLowerCase().includes(filterText.toLowerCase()) ||
        staff.fullname?.toLowerCase().includes(filterText.toLowerCase()) ||
        staff.email?.toLowerCase().includes(filterText.toLowerCase()) ||
        staff.phone_number?.toLowerCase().includes(filterText.toLowerCase())
      );
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staffList);
    }
  }, [filterText, staffList]);

  const handleEditStaff = (staff) => {
    navigate(`/edit-staff/${staff.staff_id || staff.id}`);
  };

  const handleDelete = async (staff) => {
    if (!staff) return;

    const confirmDelete = window.confirm(
      `⚠️ DELETE STAFF RECORD ⚠️\n\n` +
      `Staff ID: ${staff.staff_id || 'N/A'}\n` +
      `Name: ${staff.fullname}\n\n` +
      `This action will permanently delete this staff record.\n` +
      `This CANNOT be undone!\n\n` +
      `Are you sure you want to proceed?`
    );

    if (!confirmDelete) return;

    setDeletingStaffId(staff.id);
    try {
      // Use staff_id if available, otherwise fall back to id
      const staffIdentifier = staff.staff_id || staff.id;
      
      await api.delete(`/api/staff/${staffIdentifier}`);
      setSuccessMessage(`Staff member "${staff.fullname}" deleted successfully`);
      
      // Remove the deleted staff from the list immediately
      setStaffList(prevList => prevList.filter(s => s.id !== staff.id));
      
      // Refresh staff list
      await fetchMyStaffData();
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error deleting staff:', error);
      if (error.response?.status === 404) {
        setError('Staff record not found. It may have already been deleted.');
        await fetchMyStaffData();
      } else {
        const errorMessage = error.response?.data?.error || error.message || "Failed to delete staff record";
        setError(`Error deleting staff record: ${errorMessage}`);
      }
    } finally {
      setDeletingStaffId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">Loading Staff Data...</h4>
            <p className="text-gray-600">Please wait while we fetch the staff records.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto p-5 pt-20">
        <div className="bg-white rounded-xl shadow-lg mb-8">
          {/* Header Section with Filter */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Filter by Staff ID, Name, Email, or Phone..."
                    className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/comprehensive-staff-wizard')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  Add New Staff
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-semibold"
                >
                  ← Back to Dashboard
                </button>
              </div>
            </div>
          </div>

          {/* Success and Error Messages */}
          {successMessage && (
            <div className="mx-6 mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {successMessage}
              </div>
            </div>
          )}

          {error && (
            <div className="mx-6 mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Total Staff - Light Blue Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-sm p-4">
                <p className="text-xs font-medium text-blue-700 mb-1">Total Staff</p>
                <p className="text-2xl font-bold text-blue-800">{staffList.length}</p>
              </div>
              
              {/* Active Staff - Light Green Card */}
              <div className="bg-green-50 border border-green-200 rounded-lg shadow-sm p-4">
                <p className="text-xs font-medium text-green-700 mb-1">Active Staff</p>
                <p className="text-2xl font-bold text-green-800">
                  {staffList.filter(staff => staff.status === 'Active').length}
                </p>
              </div>
              
              {/* Inactive Staff - Light Red Card */}
              <div className="bg-red-50 border border-red-200 rounded-lg shadow-sm p-4">
                <p className="text-xs font-medium text-red-700 mb-1">Inactive Staff</p>
                <p className="text-2xl font-bold text-red-800">
                  {staffList.filter(staff => staff.status !== 'Active').length}
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          {filteredStaff.length === 0 ? (
            <div className="p-8 text-center">
           
              <h5 className="text-lg font-semibold text-gray-800 mb-2">
                {filterText ? 'No Matching Staff Found' : 'No Staff Records Available'}
              </h5>
              <p className="text-gray-600 mb-4">
                {filterText 
                  ? `No staff members match your search "${filterText}". Try adjusting your filter.`
                  : 'There are no staff records to display.'
                }
              </p>
              {filterText && (
                <button
                  onClick={() => setFilterText('')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear Filter
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Staff ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Full Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Cadre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Current Position</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredStaff.map((staff, index) => (
                    <tr 
                      key={staff.id} 
                      className={index % 2 === 0 ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white hover:bg-gray-50'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {staff.staff_id || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{staff.fullname || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {staff.email || 'No email'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {staff.phone_number || 'No phone'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {staff.current_cadre_name || staff.other_cadre || 'No cadre'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {staff.current_workposition || 'No position assigned'}
                        </div>
                        {staff.duty_location_name && (
                          <div className="text-xs text-gray-500">
                            {staff.duty_location_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          staff.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {staff.status === 'Active' ? 'Active' : 'Inactive'}
                        </span>
                        {staff.status !== 'Active' && staff.end_working_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            End: {new Date(staff.end_working_date).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleEditStaff(staff)}
                            className="bg-blue-600 text-white px-3 py-2 rounded text-xs hover:bg-blue-700 transition-colors"
                          >
                            Edit
                          </button>
                          
                          <button
                            onClick={() => handleDelete(staff)}
                            disabled={deletingStaffId === staff.id}
                            className="bg-red-600 text-white px-3 py-2 rounded text-xs hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            {deletingStaffId === staff.id ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white inline-block mr-1"></div>
                                Deleting...
                              </>
                            ) : (
                              'Delete'
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffManagementList;
